import L from 'leaflet'

/**
 * Zoom molette / trackpad continu.
 *
 * Le gestionnaire natif de Leaflet zoome par paliers animés : chaque cran
 * déclenche une animation de 0,25 s vers un niveau entier, ce qui donne un
 * effet d'escalier. Ici on accumule un zoom cible fractionnaire et on
 * s'en rapproche à chaque frame (interpolation exponentielle), le point sous
 * le curseur restant fixe. Le pincement sur trackpad Mac arrive aussi en
 * événements wheel : il profite du même rendu.
 *
 * Options de carte :
 *   smoothWheelZoom        true | 'center'  (défaut true)
 *   smoothSensitivity      multiplicateur de vitesse (défaut 1)
 */
L.Map.mergeOptions({
  smoothWheelZoom: true,
  smoothSensitivity: 1
})

// Précision du zoom appliqué à la carte. La cible est arrondie de la même
// façon : accumulée en flottant, elle ne serait sinon jamais atteinte
// exactement et le mouvement ne se terminerait pas (pas de moveend, calques
// vectoriels et tuiles figés dans leur transformation CSS).
const roundZoom = (zoom) => Math.round(zoom * 1000) / 1000

L.Map.SmoothWheelZoom = L.Handler.extend({
  addHooks () {
    L.DomEvent.on(this._map._container, 'wheel', this._onWheel, this)
  },

  removeHooks () {
    L.DomEvent.off(this._map._container, 'wheel', this._onWheel, this)
    this._stop()
  },

  _onWheel (e) {
    if (!this._wheeling) this._start(e)
    this._accumulate(e)
    L.DomEvent.stop(e)
  },

  _start (e) {
    const map = this._map
    map._stop()
    this._wheeling = true
    this._moved = false
    this._mousePos = map.mouseEventToContainerPoint(e)
    this._centerPoint = map.getSize()._divideBy(2)
    this._startLatLng = map.containerPointToLatLng(this._centerPoint)
    this._mouseLatLng = map.containerPointToLatLng(this._mousePos)
    this._goalZoom = map.getZoom()
    this._prevCenter = map.getCenter()
    this._prevZoom = map.getZoom()
    this._frame = L.Util.requestAnimFrame(this._tick, this)
  },

  _accumulate (e) {
    const map = this._map
    // getWheelDelta normalise déjà les deltas par plateforme (macOS divise
    // par 3). Avec sensibilité 1 : un cran de souris ≈ 0,1 niveau sur Mac,
    // 0,15 ailleurs ; un balayage de trackpad ≈ 1 à 2 niveaux.
    const delta = L.DomEvent.getWheelDelta(e) * 0.003 * map.options.smoothSensitivity
    this._goalZoom = roundZoom(map._limitZoom(this._goalZoom + delta))
    this._mousePos = map.mouseEventToContainerPoint(e)
    this._settling = false
    clearTimeout(this._endTimer)
    // Plus d'événement depuis 120 ms : on laisse l'interpolation atteindre
    // la cible, puis on clôt le mouvement
    this._endTimer = setTimeout(() => { this._settling = true }, 120)
  },

  _tick () {
    const map = this._map
    // Si autre chose a déplacé la carte (drag, flyTo…), on s'efface
    if (!map.getCenter().equals(this._prevCenter) || map.getZoom() !== this._prevZoom) {
      this._stop()
      return
    }

    const current = map.getZoom()
    const interpolated = roundZoom(current + (this._goalZoom - current) * 0.25)
    const zoom = Math.abs(this._goalZoom - interpolated) < 0.002 ? this._goalZoom : interpolated
    const center = map.options.smoothWheelZoom === 'center'
      ? this._startLatLng
      : this._centerKeepingCursor(zoom)

    if (zoom !== current || !center.equals(map.getCenter())) {
      if (!this._moved) {
        map._moveStart(true, false)
        this._moved = true
      }
      // pinch: true → les couches se contentent de transformer (CSS) pendant
      // le geste, comme pour un pincement ; les tuiles manquantes se chargent
      // via l'événement move (throttlé) et à la fin du geste
      map._move(center, zoom, { pinch: true, round: false })
      this._prevCenter = map.getCenter()
      this._prevZoom = map.getZoom()
    }

    if (this._settling && zoom === this._goalZoom) {
      this._end()
      return
    }

    this._frame = L.Util.requestAnimFrame(this._tick, this)
  },

  // Centre tel que le point géographique sous le curseur ne bouge pas
  _centerKeepingCursor (zoom) {
    const map = this._map
    const offset = this._mousePos.subtract(this._centerPoint)
    return map.unproject(map.project(this._mouseLatLng, zoom).subtract(offset), zoom)
  },

  _end () {
    const map = this._map
    const finished = this._moved
    this._stop()
    if (finished) map._moveEnd(true)
  },

  _stop () {
    this._wheeling = false
    this._settling = false
    clearTimeout(this._endTimer)
    if (this._frame) {
      L.Util.cancelAnimFrame(this._frame)
      this._frame = null
    }
  }
})

L.Map.addInitHook('addHandler', 'smoothWheelZoom', L.Map.SmoothWheelZoom)
